import {
  deselectAccidentDeathCapitalProduct,
  deselectAccidentDisabilityCapitalProduct,
  deselectAccidentMedicalExpensesSupplementProduct,
  deselectBasicProduct,
  deselectComplementaryProduct,
  deselectComplementaryVivaProduct,
  deselectDailyCashProduct,
  deselectDentalProduct,
  deselectEmergencyCareProduct,
  deselectHospitalCapitalProduct,
  deselectHospitalDailyCashProduct,
  deselectHospitalProduct,
  deselectHospitalVivaProduct,
  deselectLegalProtectionProduct,
  deselectOutpatientComfortProduct,
  deselectOutpatientProduct,
  deselectOutpatientVivaProduct,
  deselectSicknessDeathOrDisabilityCapitalProduct,
  selectAccidentDeathCapitalProduct,
  selectAccidentDisabilityCapitalProduct,
  selectAccidentMedicalExpensesSupplementProduct,
  selectBasicProduct,
  selectComplementaryProduct,
  selectComplementaryVivaProduct,
  selectDailyCashProduct,
  selectDentalProduct,
  selectEmergencyCareProduct,
  selectHospitalCapitalProduct,
  selectHospitalDailyCashProduct,
  selectHospitalProduct,
  selectHospitalVivaProduct,
  selectLegalProtectionProduct,
  selectOutpatientComfortProduct,
  selectOutpatientProduct,
  selectOutpatientVivaProduct,
  selectSicknessDeathOrDisabilityCapitalProduct,
} from './actions';
import { ActionCreator, on, ReducerTypes } from '@ngrx/store';
import { OfferState, PersonSupplementaryInsuranceState, SupplementaryInsuranceDiscounts } from '../offer.state';
import { applyToPersonWithId } from '../offer.reducer.util';
import { SupplementaryInsurancePrices } from '../../../api';

interface WithPersonId {
  personId: number;
}

function deselectYouthPackage(offerState: OfferState, payload: WithPersonId): OfferState {
  return {
    ...offerState,
    persons: applyToPersonWithId(payload.personId, offerState.persons, (person) => ({
      ...person,
      youthPackage: undefined,
    })),
  };
}

function changeSupplementaryInsurance(
  offerState: OfferState,
  personId: number,
  reducer: (data: PersonSupplementaryInsuranceState) => PersonSupplementaryInsuranceState,
): OfferState {
  return {
    ...offerState,
    persons: applyToPersonWithId(personId, offerState.persons, (person) => ({
      ...person,
      supplementaryInsurances: reducer(person.supplementaryInsurances),
      totalPrice: undefined,
    })),
    totalPrice: undefined,
  };
}

export const mapSupplementaryInsuranceDiscounts = (
  supplementaryInsurance: PersonSupplementaryInsuranceState,
  prices: SupplementaryInsurancePrices,
): SupplementaryInsuranceDiscounts => {
  return {
    basic: supplementaryInsurance.basic ? prices.basic.discounts : undefined,
    hospital: supplementaryInsurance.hospital ? prices.hospital.discounts : undefined,
    complementary: supplementaryInsurance.complementary ? prices.complementary.discounts : undefined,
    outpatient: supplementaryInsurance.outpatient ? prices.outpatient.discounts : undefined,
    legalProtection: supplementaryInsurance.legalProtection ? prices.legal_protection.discounts : undefined,
    accidentDisabilityCapital: supplementaryInsurance.accidentDisabilityCapital
      ? prices.accident_disability_capital.discounts
      : undefined,
    dailyCash: supplementaryInsurance.dailyCash ? prices.daily_cash.discounts : undefined,
    accidentDeathCapital: supplementaryInsurance.accidentDeathCapital
      ? prices.accident_death_capital.discounts
      : undefined,
    sicknessDeathOrDisabilityCapital: supplementaryInsurance.sicknessDeathOrDisabilityCapital
      ? prices.sickness_death_or_disability_capital.discounts
      : undefined,
    dental: supplementaryInsurance.dental ? prices.dental.discounts : undefined,
    hospitalCapital: supplementaryInsurance.hospitalCapital ? prices.hospital_capital.discounts : undefined,
    hospitalDailyCash: supplementaryInsurance.hospitalDailyCash ? prices.hospital_daily_cash.discounts : undefined,
    emergencyCare: supplementaryInsurance.emergencyCare ? prices.emergency_care.discounts : undefined,
    accidentMedicalExpensesSupplement: supplementaryInsurance.accidentMedicalExpensesSupplement
      ? prices.accident_medical_expenses_supplement.discounts
      : undefined,
    outpatientComfort: supplementaryInsurance.outpatientComfort ? prices.outpatient_comfort.discounts : undefined,
  };
};

export const mapPricesToProducts = (
  supplementaryInsurance: PersonSupplementaryInsuranceState,
  prices: SupplementaryInsurancePrices,
): PersonSupplementaryInsuranceState => {
  return {
    basic: supplementaryInsurance.basic
      ? {
          ...supplementaryInsurance.basic,
          price: prices.basic.price,
        }
      : undefined,
    hospital: supplementaryInsurance.hospital
      ? {
          ...supplementaryInsurance.hospital,
          price: prices.hospital.price,
        }
      : undefined,
    hospitalViva: supplementaryInsurance.hospitalViva
      ? {
          ...supplementaryInsurance.hospitalViva,
          price: prices.hospital_viva.price,
        }
      : undefined,
    complementary: supplementaryInsurance.complementary
      ? {
          ...supplementaryInsurance.complementary,
          price: prices.complementary.price,
        }
      : undefined,
    complementaryViva: supplementaryInsurance.complementaryViva
      ? {
          ...supplementaryInsurance.complementaryViva,
          price: prices.complementary_viva.price,
        }
      : undefined,
    outpatient: supplementaryInsurance.outpatient
      ? {
          ...supplementaryInsurance.outpatient,
          price: prices.outpatient.price,
        }
      : undefined,
    outpatientViva: supplementaryInsurance.outpatientViva
      ? {
          ...supplementaryInsurance.outpatientViva,
          price: prices.outpatient_viva.price,
        }
      : undefined,
    legalProtection: supplementaryInsurance.legalProtection
      ? {
          ...supplementaryInsurance.legalProtection,
          price: prices.legal_protection.price,
        }
      : undefined,

    accidentDisabilityCapital: supplementaryInsurance.accidentDisabilityCapital
      ? {
          ...supplementaryInsurance.accidentDisabilityCapital,
          price: prices.accident_disability_capital.price,
        }
      : undefined,
    dailyCash: supplementaryInsurance.dailyCash
      ? {
          ...supplementaryInsurance.dailyCash,
          price: prices.daily_cash.price,
        }
      : undefined,
    accidentDeathCapital: supplementaryInsurance.accidentDeathCapital
      ? {
          ...supplementaryInsurance.accidentDeathCapital,
          price: prices.accident_death_capital.price,
        }
      : undefined,
    sicknessDeathOrDisabilityCapital: supplementaryInsurance.sicknessDeathOrDisabilityCapital
      ? {
          ...supplementaryInsurance.sicknessDeathOrDisabilityCapital,
          price: prices.sickness_death_or_disability_capital.price,
        }
      : undefined,
    dental: supplementaryInsurance.dental
      ? {
          ...supplementaryInsurance.dental,
          price: prices.dental.price,
        }
      : undefined,
    hospitalCapital: supplementaryInsurance.hospitalCapital
      ? {
          ...supplementaryInsurance.hospitalCapital,
          price: prices.hospital_capital.price,
        }
      : undefined,
    hospitalDailyCash: supplementaryInsurance.hospitalDailyCash
      ? {
          ...supplementaryInsurance.hospitalDailyCash,
          price: prices.hospital_daily_cash.price,
        }
      : undefined,

    emergencyCare: supplementaryInsurance.emergencyCare
      ? {
          ...supplementaryInsurance.emergencyCare,
          price: prices.emergency_care.price,
        }
      : undefined,
    accidentMedicalExpensesSupplement: supplementaryInsurance.accidentMedicalExpensesSupplement
      ? {
          ...supplementaryInsurance.accidentMedicalExpensesSupplement,
          price: prices.accident_medical_expenses_supplement.price,
        }
      : undefined,
    outpatientComfort: supplementaryInsurance.outpatientComfort
      ? {
          ...supplementaryInsurance.outpatientComfort,
          price: prices.outpatient_comfort.price,
        }
      : undefined,
  };
};

export const supplementaryInsuranceReducers: ReducerTypes<OfferState, ActionCreator[]>[] = [
  on(selectOutpatientProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      outpatient: {
        level: payload.level,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectOutpatientProduct, (offerState, payload) =>
    changeSupplementaryInsurance(deselectYouthPackage(offerState, payload), payload.personId, (insurances) => ({
      ...insurances,
      outpatient: undefined,
    })),
  ),
  on(selectOutpatientVivaProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      outpatientViva: {
        level: payload.level,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectOutpatientVivaProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      outpatientViva: undefined,
    })),
  ),
  on(selectComplementaryProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      complementary: {
        level: payload.level,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectComplementaryProduct, (offerState, payload) =>
    changeSupplementaryInsurance(deselectYouthPackage(offerState, payload), payload.personId, (insurances) => ({
      ...insurances,
      complementary: undefined,
    })),
  ),
  on(selectComplementaryVivaProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      complementaryViva: {
        level: payload.level,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectComplementaryVivaProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      complementaryViva: undefined,
    })),
  ),
  on(selectBasicProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      basic: {
        offerType: payload.offerType,
        costSharing: payload.costSharing,
        region: payload.region,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectBasicProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      basic: undefined,
    })),
  ),
  on(selectHospitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospital: {
        offerType: payload.offerType,
        costSharing: payload.costSharing,
        region: payload.region,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectHospitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(deselectYouthPackage(offerState, payload), payload.personId, (insurances) => ({
      ...insurances,
      hospital: undefined,
    })),
  ),
  on(selectHospitalVivaProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospitalViva: {
        offerType: payload.offerType,
        costSharing: payload.costSharing,
        region: payload.region,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectHospitalVivaProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospitalViva: undefined,
    })),
  ),
  on(selectHospitalCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospitalCapital: {
        capital: payload.capital,
        accidentCoverage: payload.accidentCoverage,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectHospitalCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospitalCapital: undefined,
    })),
  ),
  on(selectAccidentDeathCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentDeathCapital: {
        accidentBenefit: payload.accidentBenefit,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectAccidentDeathCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentDeathCapital: undefined,
    })),
  ),
  on(selectAccidentDisabilityCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentDisabilityCapital: {
        accidentBenefit: payload.accidentBenefit,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectAccidentDisabilityCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentDisabilityCapital: undefined,
    })),
  ),
  on(selectSicknessDeathOrDisabilityCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      sicknessDeathOrDisabilityCapital: {
        deathBenefit: payload.deathBenefit,
        disabilityBenefit: payload.disabilityBenefit,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectSicknessDeathOrDisabilityCapitalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      sicknessDeathOrDisabilityCapital: undefined,
    })),
  ),
  on(selectLegalProtectionProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      legalProtection: {
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectLegalProtectionProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      legalProtection: undefined,
    })),
  ),
  on(selectDentalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      dental: {
        level: payload.level,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectDentalProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      dental: undefined,
    })),
  ),
  on(selectHospitalDailyCashProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospitalDailyCash: {
        benefitDuration: payload.benefitDuration,
        capital: payload.capital,
        waitingPeriod: payload.waitingPeriod,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectHospitalDailyCashProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      hospitalDailyCash: undefined,
    })),
  ),
  on(selectDailyCashProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      dailyCash: {
        accidentCoverage: payload.accidentCoverage,
        capital: payload.capital,
        waitingPeriod: payload.waitingPeriod,
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectDailyCashProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      dailyCash: undefined,
    })),
  ),
  on(selectOutpatientComfortProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      outpatientComfort: {
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectOutpatientComfortProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      outpatientComfort: undefined,
    })),
  ),
  on(selectEmergencyCareProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      emergencyCare: {
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectEmergencyCareProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      emergencyCare: undefined,
    })),
  ),
  on(selectAccidentMedicalExpensesSupplementProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentMedicalExpensesSupplement: {
        price: undefined,
        insurer: payload.insurer,
      },
    })),
  ),
  on(deselectAccidentMedicalExpensesSupplementProduct, (offerState, payload) =>
    changeSupplementaryInsurance(offerState, payload.personId, (insurances) => ({
      ...insurances,
      accidentMedicalExpensesSupplement: undefined,
    })),
  ),
];
